import { useMemo } from "react";
import { DateRange, Range, RangeKeyDict } from "react-date-range";
import { isSameDay } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Loader2 } from "lucide-react";
import { SafeAvailabilityRule } from "@/app/types";

interface FunDiveCalendarProps {
  value: Range;
  onChange: (value: Range) => void;
  disabledDates?: Date[];
  minDate?: Date;
  availabilityRules: SafeAvailabilityRule[];
  loading?: boolean;
}

const FunDiveCalendar: React.FC<FunDiveCalendarProps> = ({
  value,
  onChange,
  disabledDates = [],
  minDate,
  availabilityRules,
  loading = false,
}) => {
  const disabledDatesFromRules = useMemo(() => {
    const excludedDates = availabilityRules.flatMap((rule) =>
      rule.excludedDates.map((excluded) => new Date(excluded.date))
    );
    return [...disabledDates, ...excludedDates];
  }, [availabilityRules, disabledDates]);

  const availableDates = useMemo(() => {
    const allDates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    availabilityRules.forEach((rule) => {
      const ruleStart = new Date(rule.startDate);
      const ruleEnd = new Date(rule.endDate);
      ruleStart.setHours(0, 0, 0, 0);
      ruleEnd.setHours(0, 0, 0, 0);

      if (ruleEnd < today) return;

      const excludedDatesSet = new Set(
        rule.excludedDates.map((excluded) => {
          const d = new Date(excluded.date);
          d.setHours(0, 0, 0, 0);
          return d.toDateString();
        })
      );

      const current = new Date(Math.max(ruleStart.getTime(), today.getTime()));
      current.setHours(0, 0, 0, 0);

      while (current <= ruleEnd) {
        const dayOfWeek = current.getDay() === 0 ? 7 : current.getDay();
        if (
          rule.daysOfWeek.includes(dayOfWeek) &&
          !excludedDatesSet.has(current.toDateString())
        ) {
          allDates.push(new Date(current));
        }
        current.setDate(current.getDate() + 1);
      }
    });

    return allDates;
  }, [availabilityRules]);

  const handleDateChange = (rangesByKey: RangeKeyDict) => {
    const { selection } = rangesByKey;
    if (selection.startDate && selection.endDate) {
      onChange(selection);
    }
  };

  const isDateAvailable = (date: Date) => {
    return availableDates.some((availableDate) =>
      isSameDay(availableDate, date)
    );
  };

  const isDateDisabled = (date: Date) => {
    return disabledDatesFromRules.some((disabledDate) =>
      isSameDay(disabledDate, date)
    );
  };

  const renderDayContent = (date: Date) => {
    const isAvailable = isDateAvailable(date);
    const isPastDate = date < new Date();
    const isSelected =
      value.startDate &&
      value.endDate &&
      date >= value.startDate &&
      date <= value.endDate;
      const isDisabled = !isAvailable || disabledDates.some(
        (disabledDate) => disabledDate.toDateString() === date.toDateString()
      );

    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: isSelected ? "8px" : "0",
          backgroundColor: isSelected ? "#0e7490" : "transparent",
          color: isSelected ? "white" : isDisabled ? "gray" : isPastDate ? "gray" : "black",
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
      >
        {date.getDate()}
      </div>
    );
  };

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      {loading && (
        <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
        </div>
      )}
      <div className="calendar-wrapper">
        <style jsx global>{`
          .calendar-wrapper .rdrCalendarWrapper {
            width: 100%;
            font-size: 16px;
          }
          .calendar-wrapper .rdrMonth {
            width: 100%;
          }
          .calendar-wrapper .rdrDayNumber {
            font-size: 16px;
          }
          .calendar-wrapper .rdrDay {
            height: 50px;
          }
          .calendar-wrapper .rdrDayToday .rdrDayNumber span:after {
            background: #0e7490;
          }
          .calendar-wrapper .rdrDayDisabled {
            background-color: transparent;
          }
          .calendar-wrapper .rdrDayDisabled span {
            color: #d4d4d4;
          }
          .calendar-wrapper .rdrMonthAndYearWrapper {
            padding-top: 10px;
            padding-bottom: 10px;
            height: auto;
          }
          .calendar-wrapper .rdrNextPrevButton {
            margin: 0;
          }
          .calendar-wrapper .rdrDateDisplayWrapper {
            display: none;
          }
        `}</style>
        <DateRange
          ranges={[value]}
          onChange={handleDateChange}
          minDate={minDate || new Date()}
          disabledDay={(date) => isDateDisabled(date) || !isDateAvailable(date)}
          rangeColors={["#0e7490"]}
          className="rounded-md"
          dayContentRenderer={renderDayContent}
        />
      </div>
    </div>
  );
};

export default FunDiveCalendar;